import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { App as CapApp } from "@capacitor/app";

const SENTINEL_STATE = { rim_back_sentinel: true };
const ROOT_PATHS = new Set(["/", "/dashboard", "/login"]);

/**
 * Keeps the browser / Android Back button inside the app.
 *
 * Problem: in PWA standalone mode and the Capacitor shell the history
 * stack starts empty, so the first Back press exits the app instead of
 * going to the previous in-app page.
 *
 * Fix (browser/PWA): a "sentinel" history entry is pushed at app start.
 * In-app <Link>/navigate() pushes stack above it, so Back traverses pages
 * normally; when Back pops all the way down to the sentinel (deepest
 * page), we immediately push a fresh sentinel on top — Back then stays on
 * the current page instead of exiting.
 *
 * Fix (Capacitor Android): the hardware back button calls history.back()
 * on inner pages and minimizes the app on root pages.
 */
export default function BackNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const armed = useRef(false);
  const firstArmLanded = useRef(false);

  // Arm once per app load.
  useEffect(() => {
    if (armed.current) return;
    armed.current = true;
    navigate(`${location.pathname}${location.search}${location.hash}`, { state: SENTINEL_STATE });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Landing on the sentinel means Back was pressed at the deepest in-app
  // page — re-arm so the app never exits. The first landing is the initial
  // arm itself, which we skip.
  useEffect(() => {
    if (!location.state?.rim_back_sentinel) return;
    if (!firstArmLanded.current) {
      firstArmLanded.current = true;
      return;
    }
    navigate(`${location.pathname}${location.search}${location.hash}`, { state: SENTINEL_STATE });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Android hardware back button.
  useEffect(() => {
    if (!window.Capacitor?.isNativePlatform?.()) return;
    let handle;
    CapApp.addListener("backButton", () => {
      if (ROOT_PATHS.has(window.location.pathname)) {
        CapApp.minimizeApp();
      } else {
        window.history.back();
      }
    }).then((h) => { handle = h; });
    return () => { handle?.remove?.(); };
  }, []);

  // PC keyboard: Backspace acts as browser Back (classic behaviour modern
  // browsers dropped). Never hijack it while the user is typing — inputs,
  // textareas, selects and contentEditable regions keep their normal
  // delete-character behaviour.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "Backspace") return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      const t = e.target;
      if (!t) return;
      const tag = t.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (t.isContentEditable) return;
      e.preventDefault();
      window.history.back();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return null;
}
